import { useState } from 'react';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import { Bell, Shield, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { accountAPI, authAPI } from '../../services/api';
import {
  VALIDATION_MESSAGES,
  REGEX_PATTERNS,
} from '../../utils/validationMessages';
import PageHeader from '../../components/common/PageHeader';
import LoaderOverlay from '../../components/common/LoaderOverlay';

import SettingsTabs, {
  TabConfig,
} from '../../components/settings/SettingsTabs';
import ChangePassword from '../../components/settings/ChangePassword';
import SecurityStatus from '../../components/settings/SecurityStatus';
import TwoFactorAuth from '../../components/settings/TwoFactorAuth';
import SessionManagement from '../../components/settings/SessionManagement';
import NotificationTypes from '../../components/settings/NotificationTypes';
import NotificationChannels from '../../components/settings/NotificationChannels';
import ProfileVisibility from '../../components/settings/ProfileVisibility';
import DataPrivacy from '../../components/settings/DataPrivacy';

interface UserSettingsState {
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    marketing: boolean;
    security: boolean;
    updates: boolean;
    trading: boolean;
  };
  security: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
    twoFactor: boolean;
    sessionTimeout: string;
  };
  privacy: {
    profileVisibility: 'public' | 'private';
    showEmail: boolean;
    showPhone: boolean;
    showTradingStats: boolean;
    allowMessages: boolean;
  };
}

interface PasswordFormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState('notifications');
  const [isSaving, setIsSaving] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<PasswordFormErrors>({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const tabs: TabConfig[] = [
    {
      value: 'notifications',
      label: 'Notifications',
      shortLabel: 'Notify',
      icon: <Bell className="h-4 w-4" />,
    },
    {
      value: 'security',
      label: 'Security',
      icon: <Shield className="h-4 w-4" />,
    },
    { value: 'privacy', label: 'Privacy', icon: <Eye className="h-4 w-4" /> },
  ];

  const [settings, setSettings] = useState<UserSettingsState>({
    notifications: {
      email: true,
      sms: false,
      push: true,
      marketing: false,
      security: true,
      updates: true,
      trading: true,
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactor: false,
      sessionTimeout: '30',
    },
    privacy: {
      profileVisibility: 'private',
      showEmail: false,
      showPhone: false,
      showTradingStats: false,
      allowMessages: false,
    },
  });

  const handleInputChange = (
    section: keyof UserSettingsState,
    field: string,
    value: string | boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const isPasswordFormValid = () => {
    const { currentPassword, newPassword, confirmPassword } = settings.security;
    return (
      currentPassword.trim() !== '' &&
      newPassword.trim() !== '' &&
      confirmPassword.trim() !== '' &&
      !passwordErrors.currentPassword &&
      !passwordErrors.newPassword &&
      !passwordErrors.confirmPassword &&
      newPassword.length >= 8 &&
      newPassword.length <= 50 &&
      REGEX_PATTERNS.password.strong.test(newPassword) &&
      newPassword === confirmPassword
    );
  };

  const validatePasswordField = (field: string, value: string) => {
    const newErrors: PasswordFormErrors = { ...passwordErrors };

    if (field === 'currentPassword') {
      if (!value.trim()) {
        newErrors.currentPassword = 'Current password is required';
      } else {
        newErrors.currentPassword = undefined;
      }
    }

    if (field === 'newPassword') {
      if (!value.trim()) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.required;
      } else if (value.length < 8) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.minLength;
      } else if (value.length > 50) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.maxLength;
      } else if (!REGEX_PATTERNS.password.strong.test(value)) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.weak;
      } else {
        newErrors.newPassword = undefined;
      }

      if (settings.security.confirmPassword) {
        if (value !== settings.security.confirmPassword) {
          newErrors.confirmPassword =
            VALIDATION_MESSAGES.confirmPassword.mismatch;
        } else {
          newErrors.confirmPassword = undefined;
        }
      }
    }

    if (field === 'confirmPassword') {
      if (!value.trim()) {
        newErrors.confirmPassword =
          VALIDATION_MESSAGES.confirmPassword.required;
      } else if (settings.security.newPassword !== value) {
        newErrors.confirmPassword =
          VALIDATION_MESSAGES.confirmPassword.mismatch;
      } else {
        newErrors.confirmPassword = undefined;
      }
    }

    setPasswordErrors(newErrors);
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    handleInputChange('security', field, value);
    validatePasswordField(field, value);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestData = async () => {
    setIsDownloading(true);
    try {
      const blob = await accountAPI.exportData();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'forex-crm-data.xlsx';
      link.click();

      window.URL.revokeObjectURL(url);

      toast.success('Data downloaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download data');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.error('Account deletion requires additional verification');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = settings.security;
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    setIsSaving(true);
    try {
      const response = await authAPI.changePassword({
        currentPassword,
        newPassword,
      });
      if (response.success || response.status === true) {
        toast.success(response.message || 'Password changed successfully');
        setSettings(prev => ({
          ...prev,
          security: {
            ...prev.security,
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          },
        }));
      } else {
        toast.error(response.message || 'Failed to change password');
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to change password';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 px-4">
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <div className="sticky top-0 z-20 bg-background pt-4">
          <PageHeader
            title="Account Settings"
            description="  Manage your account settings and preferences"
          />
          <div className="mt-4">
            <SettingsTabs tabs={tabs} />
          </div>
        </div>
        <TabsContent value="notifications">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NotificationChannels
              title="Notification Channels"
              description="Choose how you want to receive notifications"
              items={[
                {
                  key: 'email',
                  label: 'Email Notifications',
                  description: 'Receive notifications via email',
                  enabled: settings.notifications.email,
                },
                {
                  key: 'sms',
                  label: 'SMS Notifications',
                  description: 'Receive notifications via SMS',
                  enabled: settings.notifications.sms,
                },
                {
                  key: 'push',
                  label: 'Push Notifications',
                  description: 'Receive push notifications',
                  enabled: settings.notifications.push,
                },
              ]}
              onToggle={key =>
                handleInputChange(
                  'notifications',
                  key,
                  !settings.notifications[
                    key as keyof typeof settings.notifications
                  ]
                )
              }
            />

            <NotificationTypes
              items={[
                {
                  key: 'trading',
                  label: 'Trading Alerts',
                  description: 'Price alerts and trade notifications',
                  enabled: settings.notifications.trading,
                },
                {
                  key: 'security',
                  label: 'Security Alerts',
                  description: 'Security and login notifications',
                  enabled: settings.notifications.security,
                },
                {
                  key: 'updates',
                  label: 'Product Updates',
                  description: 'New features and updates',
                  enabled: settings.notifications.updates,
                },
                {
                  key: 'marketing',
                  label: 'Marketing',
                  description: 'Marketing and promotional emails',
                  enabled: settings.notifications.marketing,
                },
              ]}
              onToggle={key =>
                handleInputChange(
                  'notifications',
                  key,
                  !settings.notifications[
                    key as keyof typeof settings.notifications
                  ]
                )
              }
              onSave={handleSave}
              isSaving={isSaving}
              description="Select the types of notifications you want to receive"
            />
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              <LoaderOverlay
                isLoading={isSaving}
                loadingText="Updating password..."
              >
                <ChangePassword
                  currentPassword={settings.security.currentPassword}
                  newPassword={settings.security.newPassword}
                  confirmPassword={settings.security.confirmPassword}
                  passwordErrors={passwordErrors}
                  isSaving={isSaving}
                  onCurrentPasswordChange={value =>
                    handlePasswordInputChange('currentPassword', value)
                  }
                  onNewPasswordChange={value =>
                    handlePasswordInputChange('newPassword', value)
                  }
                  onConfirmPasswordChange={value =>
                    handlePasswordInputChange('confirmPassword', value)
                  }
                  onSubmit={handlePasswordChange}
                  isPasswordFormValid={isPasswordFormValid}
                  description="Update your password to keep your account secure"
                />
              </LoaderOverlay>

              <TwoFactorAuth
                twoFactorEnabled={settings.security.twoFactor}
                onToggle={() =>
                  handleInputChange(
                    'security',
                    'twoFactor',
                    !settings.security.twoFactor
                  )
                }
                description="Add an extra layer of security to your account"
                contentDescription="Use an authenticator app for enhanced security"
              />
            </div>

            <div className="space-y-6">
              <SecurityStatus twoFactorEnabled={settings.security.twoFactor} />

              <SessionManagement />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="privacy">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ProfileVisibility
              privacy={settings.privacy}
              onInputChange={(field, value) =>
                handleInputChange('privacy', field, value)
              }
              onSave={handleSave}
              isSaving={isSaving}
            />

            <DataPrivacy
              isDeleting={isDeleting}
              isDownloading={isDownloading}
              onRequestData={handleRequestData}
              onDeleteAccount={handleDeleteAccount}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountSettings;
